import React, { useState, useMemo, useCallback, useEffect } from "react";
import styles from "./Users.module.css";
import UserList from "../../components/users/UserList/UserList";
import UserForm from "../../components/users/UserForm/UserForm";
import Button from "../../components/common/Button/Button";
import Modal from "../../components/common/Modal/Modal";
import FullscreenModal from "../../components/common/FullscreenModal/FullscreenModal";
import { useUsers } from "../../hooks/useUsers";
import api from "../../services/api";
import { formatAdminDateTime } from "../../utils/datetime";

const Users = () => {
  const {
    users,
    selectedUser,
    loading,
    error,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    searchUsers,
    clearSelectedUser,
    clearError,
  } = useUsers();

  const [page, setPage] = useState(1);
  const limit = 10;
  const [roles, setRoles] = useState([]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState({});

  // Track form fields for progress calculation
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  // Calculate form completion progress
  const formProgress = useMemo(() => {
    const fields = [formData.name, formData.email, formData.phone];
    const filledFields = fields.filter((field) => field?.trim()).length;
    return (filledFields / fields.length) * 100;
  }, [formData]);

  // ✅ Fetch roles once on mount
  useEffect(() => {
    api.get("/access-roles").then((res) => {
      setRoles(res.data?.data || res.data || []);
    }).catch((err) => console.error("Error fetching roles:", err));
  }, []);

  // ✅ Fetch users when page changes or filters change
  useEffect(() => {
    const hasFilters = activeFilters.role || activeFilters.isresearchanalyst || activeFilters.createdFrom || activeFilters.createdTo;
    if (!searchTerm.trim() && !hasFilters) {
      fetchUsers(page, limit);
    } else {
      searchUsers(searchTerm, { ...activeFilters, limit, offset: (page - 1) * limit });
    }
  }, [page, limit, searchTerm, activeFilters, fetchUsers, searchUsers]);

  // Handle form data changes from UserForm
  const handleFormDataChange = useCallback((newFormData) => {
    setFormData(newFormData);
  }, []);

  const handleCreateUser = async (userData) => {
    try {
      await createUser(userData);
      setShowCreateModal(false);
      setFormData({ name: "", email: "", phone: "" }); // Reset form data
    } catch (err) {
      console.error("Failed to create user:", err);
    }
  };

  const handleEditUser = (user) => {
    // Parse preference arrays if they're stored as strings
    const parsePreferenceField = (field) => {
      if (!field) return [];
      if (Array.isArray(field)) return field;
      if (typeof field === "string") {
        try {
          // Try parsing as JSON first
          const parsed = JSON.parse(field);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          // If not JSON, try comma-separated values
          return field
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        }
      }
      return [];
    };

    // Parse agreements from database response
    const parseAgreements = (agreements) => {
      if (!agreements) return [];
      if (Array.isArray(agreements)) return agreements;
      if (typeof agreements === "string") {
        try {
          const parsed = JSON.parse(agreements);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      return [];
    };

    // Prepare user data with properly parsed arrays and agreements
    const userWithParsedData = {
      ...user,
      // Convert old field names to new field names if needed
      age: user.age || "",
      gender: user.gender || user.genderid || "",
      prefsectors: parsePreferenceField(user.prefsectors || user.prefstockids),
      prefindustries: parsePreferenceField(user.prefindustries),
      prefstocks: parsePreferenceField(user.prefstocks || user.prefstockids),
      needguidance: user.needguidance || "",
      experienceinmarket: user.experienceinmarket || "",
      preferenceonapp: parsePreferenceField(
        user.preferenceonapp || user.preferenceids
      ),
      timelinecontent: parsePreferenceField(
        user.timelinecontent || user.timelinecontentids
      ),
      // Parse agreements
      agreements: parseAgreements(user.agreements),
    };


    // Set the user to edit and open modal
    setUserToEdit(userWithParsedData);
    setFormData({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async (userData) => {
    if (!userToEdit) return;

    try {
      await updateUser(userToEdit.userid, userData);
      setShowEditModal(false);
      setUserToEdit(null);
      setFormData({ name: "", email: "", phone: "" }); // Reset form data
    } catch (err) {
      console.error("Failed to update user:", err);
    }
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = async () => {
    if (userToDelete) {
      try {
        await deleteUser(userToDelete.userid);
        setShowDeleteModal(false);
        setUserToDelete(null);
      } catch (err) {
        console.error("Failed to delete user:", err);
      }
    }
  };

  const handleViewUser = (user) => {
    // Navigate to user detail view or show user details modal
    // You could implement a user detail modal or navigation here
  };

  const handleSearch = (term, filters = {}) => {
    setSearchTerm(term);
    setActiveFilters(filters);
    setPage(1); // ✅ Reset to first page when searching
  };

  const handleRefresh = () => {
    const hasFilters = activeFilters.role || activeFilters.isresearchanalyst || activeFilters.createdFrom || activeFilters.createdTo;
    if (searchTerm.trim() || hasFilters) {
      searchUsers(searchTerm, { ...activeFilters, limit, offset: (page - 1) * limit });
    } else {
      fetchUsers(page, limit); // ✅ Pass current page
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setUserToEdit(null);
    setFormData({ name: "", email: "", phone: "" });
    clearSelectedUser();
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setFormData({ name: "", email: "", phone: "" });
  };

  const isEditing = !!userToEdit;
  const isDirty = false; // This will be handled by the form itself

  // Modal titles and subtitles
  const createModalTitle = "Create New User";
  const createModalSubtitle =
    "Fill in the details to create a new user account with preferences and RA agreements";
  const editModalTitle = "Edit User";
  const editModalSubtitle = userToEdit
    ? `Editing ${
        userToEdit.name || "User"
      }'s information, preferences, and RA agreements`
    : "Update user information, preferences, and RA agreements";

  // Helper function to get agreement summary for deletion warning
  const getAgreementSummary = (user) => {
    if (!user?.agreements || user.agreements.length === 0) {
      return null;
    }

    const agreements = Array.isArray(user.agreements) ? user.agreements : [];
    const activeAgreements = agreements.filter(
      (a) => a.agreementStatus === "signed"
    ).length;
    const totalAgreements = agreements.length;

    return { active: activeAgreements, total: totalAgreements };
  };

  return (
    <div className={styles.usersPage}>
      {/* <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerLeft}>
            <h1 className={styles.pageTitle}>User Management</h1>
            <p className={styles.pageSubtitle}>
              Manage users, their preferences, trading profiles, and RA
              agreements in your stock community
            </p>
          </div>
          <div className={styles.headerActions}>
            <Button
              variant="primary"
              onClick={() => setShowCreateModal(true)}
              icon="+"
            >
              Add New User
            </Button>
          </div>
        </div>
      </div> */}

      {error && (
        <div className={styles.errorBanner}>
          <span className={styles.errorIcon}>⚠️</span>
          <span className={styles.errorMessage}>{error.message}</span>
          <button
            className={styles.errorClose}
            onClick={clearError}
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      <div className={styles.pageContent}>
        <UserList
          users={users}
          loading={loading}
          roles={roles}
          onEdit={handleEditUser}
          onDelete={handleDeleteUser}
          onView={handleViewUser}
          onSearch={handleSearch}
          onRefresh={handleRefresh}
          onAddUser={() => setShowCreateModal(true)}
        >
          {/* Pagination */}
          <div className={styles.imTableFooter}>
            <span>
              Showing {users.length} users
            </span>

            <div className={styles.imPagination}>
              <button
                type="button"
                className={styles.imPageBtn}
                disabled={page === 1 || loading}
                onClick={() => setPage(page - 1)}
              >
                {"<"}
              </button>

              {/* Show page 1 if we're far from it */}
              {page > 2 && (
                <button
                  type="button"
                  className={styles.imPageBtn}
                  onClick={() => setPage(1)}
                >
                  1
                </button>
              )}

              {/* Ellipsis before current range */}
              {page > 3 && (
                <span className={styles.imPageEllipsis}>...</span>
              )}

              {/* Previous page */}
              {page > 1 && (
                <button
                  type="button"
                  className={styles.imPageBtn}
                  onClick={() => setPage(page - 1)}
                >
                  {page - 1}
                </button>
              )}

              {/* Current page */}
              <button
                type="button"
                className={`${styles.imPageBtn} ${styles.active}`}
              >
                {page}
              </button>

              {/* Next page (only if current page has full results) */}
              {users.length >= limit && (
                <button
                  type="button"
                  className={styles.imPageBtn}
                  onClick={() => setPage(page + 1)}
                >
                  {page + 1}
                </button>
              )}

              <button
                type="button"
                className={styles.imPageBtn}
                disabled={users.length < limit || loading}
                onClick={() => setPage(page + 1)}
              >
                {">"}
              </button>
            </div>
            
            <span className={styles.refreshTime}>
              Last refreshed: {formatAdminDateTime(new Date())}
            </span>
          </div>
        </UserList>
      </div>

      {/* Create User Fullscreen Modal */}
      <FullscreenModal
        isOpen={showCreateModal}
        onClose={handleCloseCreateModal}
        title={createModalTitle}
        subtitle={createModalSubtitle}
        size="fullscreen"
        showProgress={true}
        progress={formProgress}
        isDirty={isDirty}
        loading={loading}
        disableBackdropClick={true}
      >
        <UserForm
          onSubmit={handleCreateUser}
          onCancel={handleCloseCreateModal}
          onFormDataChange={handleFormDataChange}
          loading={loading}
          fullscreenMode={true}
        />
      </FullscreenModal>

      {/* Edit User Fullscreen Modal */}
      <FullscreenModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        title={editModalTitle}
        subtitle={editModalSubtitle}
        size="fullscreen"
        showProgress={true}
        progress={formProgress}
        isDirty={isDirty}
        loading={loading}
        disableBackdropClick={true}
      >
        <UserForm
          user={userToEdit}
          onSubmit={handleUpdateUser}
          onCancel={handleCloseEditModal}
          onFormDataChange={handleFormDataChange}
          loading={loading}
          fullscreenMode={true}
        />
      </FullscreenModal>

      {/* Delete Confirmation Modal - Keep as regular modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setUserToDelete(null);
        }}
        title="Confirm Deletion"
        size="small"
        closeOnOverlay={false}
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setUserToDelete(null);
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={confirmDeleteUser}
              loading={loading}
            >
              Delete User
            </Button>
          </>
        }
      >
        <div className={styles.deleteConfirmation}>
          <div className={styles.warningIcon}>🚨</div>
          <p>
            Are you sure you want to delete{" "}
            <strong>{userToDelete?.name}</strong>?
          </p>
          <p className={styles.warningText}>
            This action cannot be undone. All user data, preferences, and
            associated records will be permanently removed.
          </p>

          {userToDelete?.isresearchanalyst === 1 && (
            <>
              <p className={styles.warningText}>
                <strong>Note:</strong> This user is a Research Analyst. Their RA
                data will also be deleted.
              </p>

              {(() => {
                const agreementSummary = getAgreementSummary(userToDelete);
                return (
                  agreementSummary && (
                    <p className={styles.warningText}>
                      <strong>RA Agreements:</strong> This will delete{" "}
                      {agreementSummary.total} agreement
                      {agreementSummary.total !== 1 ? "s" : ""}
                      {agreementSummary.active > 0 &&
                        ` (including ${
                          agreementSummary.active
                        } active agreement${
                          agreementSummary.active !== 1 ? "s" : ""
                        })`}
                      .
                    </p>
                  )
                );
              })()}
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Users;
